import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"
import { Button } from "@/modules/shared/ui/components/button"
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/modules/shared/ui/components/card"
import { Input } from "@/modules/shared/ui/components/input"
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/modules/shared/ui/components/form"
import { Link } from "react-router-dom"

const formSchema = z.object({
    email: z.email({
        message: "Please enter a valid email address.",
    }),
})

type FormValues = z.infer<typeof formSchema>

interface ForgotPasswordFormProps {
    onSubmit: (email: string) => Promise<void>
    isLoading?: boolean
    errorMessage?: string
    isCoolingDown?: boolean
}

export default function ForgotPasswordForm({
    onSubmit,
    isLoading = false,
    errorMessage = "",
    isCoolingDown = false,
}: ForgotPasswordFormProps) {
    const form = useForm<FormValues>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            email: "",
        },
    })

    const handleSubmit = async (values: FormValues) => {
        await onSubmit(values.email)
    }

    return (
        <div className="w-full md:w-[75%] justify-center items-center">
            <Card className="shadow-none border-0 w-full">
                <CardHeader>
                    <CardTitle className="text-3xl">Forgot your password?</CardTitle>
                    <CardDescription className="text-base text-muted-foreground">
                        Enter your email address and we'll send you a link to reset your password.
                    </CardDescription>
                </CardHeader>

                <CardContent>
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-5">
                            <FormField
                                control={form.control}
                                name="email"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="text-foreground font-medium">
                                            Email Address
                                        </FormLabel>
                                        <FormControl>
                                            <Input
                                                type="email"
                                                placeholder="Enter your email address"
                                                className="border-border focus:border-blue-500 focus:ring-blue-500"
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage className="text-red-500" />
                                    </FormItem>
                                )}
                            />

                            {errorMessage && (
                                <p className="text-sm text-red-600 mt-1">{errorMessage}</p>
                            )}

                            <Button
                                type="submit"
                                className="w-full text-white py-6 text-base"
                                style={{ backgroundColor: "#1A237E" }}
                                disabled={isLoading || isCoolingDown}
                            >
                                {isCoolingDown
                                    ? "Try again later"
                                    : isLoading
                                        ? "Sending..."
                                        : "Send Reset Link"}
                            </Button>

                            <div className="text-center">
                                <Link
                                    to="/login"
                                    className="text-sm font-medium hover:underline"
                                    style={{ color: "#1A237E" }}
                                >
                                    Back to Login
                                </Link>
                            </div>
                        </form>
                    </Form>
                </CardContent>
            </Card>
        </div>
    )
}
